import {
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll,
  WSList
} from "@wingspanhq/fe-component-library";
import { Statement } from "@wingspanhq/wingspan-api-sdk";
import { useCallback, useMemo } from "react";
import { queriesInternalAccounts } from "@query";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryMemberProfile } from "../../../../query/users/queries";
import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import { accountTypeToHumanReadable } from "../../utils/accountTypeToHumanReadable";
import { buildStatementsFiltersConfig } from "./filters/config";
import {
  DEFAULT_STATEMENT_ACCOUNT_TYPE,
  StatementsFilter
} from "./filters/types";
import { TableStatements } from "./TableStatements";
import { useMutationDownloadStatement } from "./useMutationDownloadStatement";

export const FilterableTableStatements: React.FC = () => {
  const userId = useUserId();
  const queryMember = useQueryMemberProfile(userId);

  const { urlQuery, setUrlQuery } = useUrlQuery<StatementsFilter>();

  const accountType =
    urlQuery.filter.accountType ?? DEFAULT_STATEMENT_ACCOUNT_TYPE;

  const filtersConfig = useMemo(() => buildStatementsFiltersConfig(), []);

  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery({
      filter: { type: { eq: accountType } }
    });

  const internalAccount = queryInternalAccounts.data?.data?.find(
    account => account.type === accountType
  );
  const hasAccount = !!internalAccount?.id;

  // The v3 statements endpoint paginates (cursor) but has no filter/sort
  // params — so the table is server-paginated, in server order, with no
  // client-side filtering or sorting.
  const queryStatements =
    queriesInternalAccounts.listInternalAccountStatements.usePaginatedQuery(
      { internalAccountId: internalAccount?.id ?? "" },
      { enabled: hasAccount }
    );

  const statements = queryStatements.flatData ?? [];

  // Total comes from the list response's pagination summary — not
  // statements.length, which is only the rows loaded so far under pagination.
  const totalCount = queryStatements.data?.pages?.[0]?.pagination?.totalSize;

  const [download, downloadMeta] = useMutationDownloadStatement();

  const onDownload = useCallback(
    async (statement: Statement) => {
      if (!internalAccount || !queryMember.data) {
        return;
      }
      await download({
        statementId: statement.id,
        internalAccount,
        member: queryMember.data
      });
    },
    [download, internalAccount, queryMember.data]
  );

  // The statements query is disabled until an account is resolved, so only
  // factor in its loading state when there actually is an account to load.
  const isLoading =
    queryInternalAccounts.isLoading ||
    queryMember.isLoading ||
    (hasAccount && queryStatements.isLoading);

  const renderContent = () => {
    if (isLoading) {
      return (
        <TableStatements
          statements={statements}
          internalAccount={internalAccount}
          loading
          onDownload={onDownload}
        />
      );
    }

    if (!hasAccount) {
      return (
        <WSEmptyState
          type="wallet"
          title={`No ${accountTypeToHumanReadable(accountType)} account`}
          description="You don't have an account of this type, so there are no statements to show."
        />
      );
    }

    if (statements.length === 0) {
      return (
        <WSEmptyState
          type="search"
          title="No statement available yet"
          description="Statements will appear here once available."
        />
      );
    }

    return (
      <WSInfiniteScroll
        onLoad={() => queryStatements.fetchNextPage()}
        loadMore={statements.length > 0}
        endOfList={!queryStatements.hasNextPage}
        loading={!!queryStatements.isFetchingNextPage}
      >
        <TableStatements
          statements={statements}
          internalAccount={internalAccount}
          downloadDisabled={downloadMeta.isLoading || !queryMember.data}
          onDownload={onDownload}
        />
      </WSInfiniteScroll>
    );
  };

  return (
    <WSList gap="2XL">
      <WSFilters<StatementsFilter>
        values={{ ...urlQuery.filter, accountType }}
        onFilter={newFilter => {
          setUrlQuery(prev => ({ ...prev, filter: newFilter }));
        }}
        primaryFilters={filtersConfig}
        info={totalCount === undefined ? undefined : { count: totalCount }}
      />

      {renderContent()}
    </WSList>
  );
};
